import './link-style.scss';

import { Slot, Slottable } from '@radix-ui/react-slot';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';

import { cn } from '../../utils/helpers.ts';

export type LinkStyleProps = React.PropsWithChildren<{
  disabled?: boolean;
  fullWidth?: boolean;
  iconAfter?: IconNames;
  iconBefore?: IconNames;
  inline?: boolean;
  noPadding?: boolean;
  noUnderline?: boolean;
  purpose?:
    | 'positive'
    | 'neutral'
    | 'warning'
    | 'negative'
    | 'disabled'
    | 'invert';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  variant?: 'primary' | 'invert';
  weightOverride?: 'normal' | 'light';
}>;

/**
 * HOC(ish) that is responsible for applying design system link styling directly to its child.
 * It is only concerned with adding the necessary classes to whatever child is passed to it,
 * and is agnostic as to what kind of HTML element is being rendered.
 */
export const LinkStyle: React.FC<
  LinkStyleProps & { ref?: React.Ref<HTMLDivElement> }
> = ({
  disabled,
  fullWidth,
  iconBefore,
  iconAfter,
  inline,
  noPadding,
  noUnderline,
  purpose,
  size = 'md',
  variant = 'primary',
  weightOverride,
  children,
  ref,
  ...props
}) => {
  return (
    <Slot
      ref={ref}
      {...props}
      // Hack to make TypeScript happy
      {...(disabled && {
        disabled: true,
      })}
      // Overwrite className and style to prevent anyone altering styles.
      className={cn(`LinkStyle LinkStyle--${variant} LinkStyle--${size}`, {
        [`LinkStyle--${purpose}`]: !!purpose && purpose !== 'disabled',
        [`LinkStyle--${weightOverride}`]: !!weightOverride,
        'LinkStyle--disabled': purpose === 'disabled' || disabled,
        'LinkStyle--inline': inline,
        'LinkStyle--noPadding': noPadding,
        'LinkStyle--noUnderline': noUnderline,
        'LinkStyle--fullWidth': fullWidth,
        'LinkStyle--icon': iconBefore || iconAfter,
      })}
      style={undefined}
    >
      {iconBefore && (
        // TODO: 1.5em seems a little big (and enforcing it seems to remove some flexibility)
        <ArkIcon iconName={iconBefore} size="1.5em" />
      )}
      <Slottable>{children}</Slottable>
      {iconAfter && <ArkIcon iconName={iconAfter} size="1.5em" />}
    </Slot>
  );
};
